import { CampanhaSimulada } from '../../types/simulador';

interface CustoAnalyzerProps {
  campanha: CampanhaSimulada;
}

export const CustoAnalyzer = ({ campanha }: CustoAnalyzerProps) => {
  const { analiseCustos, analiseComplexidade } = campanha;

  const getComplexidadeColor = (nivel: string) => {
    switch (nivel) {
      case 'Simples': return 'text-green-600 bg-green-100';
      case 'Moderada': return 'text-blue-600 bg-blue-100';
      case 'Complexa': return 'text-orange-600 bg-orange-100';
      case 'Muito Complexa': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-money-dollar-circle-line text-3xl opacity-80"></i>
            <span className="text-xs opacity-80">Total</span>
          </div>
          <p className="text-3xl font-bold">
            R$ {analiseCustos.custoTotalRecompensas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-sm opacity-90 mt-1">Custo Total de Recompensas</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-user-line text-3xl opacity-80"></i>
            <span className="text-xs opacity-80">Por Participante</span>
          </div>
          <p className="text-3xl font-bold">
            R$ {analiseCustos.custoMedioPorParticipante.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-sm opacity-90 mt-1">Custo Médio</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <i className="ri-dashboard-line text-3xl opacity-80"></i>
            <span className="text-xs opacity-80">Complexidade</span>
          </div>
          <p className="text-3xl font-bold">{analiseComplexidade.indiceComplexidade}</p>
          <p className="text-sm opacity-90 mt-1">{analiseComplexidade.nivelComplexidade}</p>
        </div>
      </div>

      {/* Análise de Complexidade */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-pie-chart-line text-orange-600"></i>
          Análise de Complexidade
        </h3>
        
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Índice de Complexidade</span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getComplexidadeColor(analiseComplexidade.nivelComplexidade)}`}>
              {analiseComplexidade.nivelComplexidade}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                analiseComplexidade.indiceComplexidade < 25 ? 'bg-green-500' :
                analiseComplexidade.indiceComplexidade < 50 ? 'bg-blue-500' :
                analiseComplexidade.indiceComplexidade < 75 ? 'bg-orange-500' :
                'bg-red-500'
              }`}
              style={{ width: `${analiseComplexidade.indiceComplexidade}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">{analiseComplexidade.indiceComplexidade}/100</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="ri-target-line text-2xl text-orange-600 mb-2"></i>
            <p className="text-2xl font-bold text-gray-900">{analiseComplexidade.totalMetas}</p>
            <p className="text-sm text-gray-600">Metas</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="ri-medal-line text-2xl text-orange-600 mb-2"></i>
            <p className="text-2xl font-bold text-gray-900">{analiseComplexidade.totalDistintivos}</p>
            <p className="text-sm text-gray-600">Distintivos</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="ri-flag-line text-2xl text-orange-600 mb-2"></i>
            <p className="text-2xl font-bold text-gray-900">{analiseComplexidade.totalMissoes}</p>
            <p className="text-sm text-gray-600">Missões</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <i className="ri-gift-line text-2xl text-orange-600 mb-2"></i>
            <p className="text-2xl font-bold text-gray-900">{analiseComplexidade.totalRecompensas}</p>
            <p className="text-sm text-gray-600">Recompensas</p>
          </div>
        </div>
      </div>

      {/* Custo por Nível */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-box-line text-orange-600"></i>
          Distribuição de Custos por Nível
        </h3>
        <div className="space-y-3">
          {Object.entries(analiseCustos.custoPorNivel).map(([nivel, custo]) => {
            const percentual = (custo / analiseCustos.custoTotalRecompensas) * 100;
            return (
              <div key={nivel}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{nivel}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    R$ {custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all"
                    style={{ width: `${percentual}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{percentual.toFixed(1)}% do total</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribuição por Tipo de Recompensa */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-donut-chart-line text-orange-600"></i>
          Distribuição por Tipo de Recompensa
        </h3>
        <div className="space-y-3">
          {analiseCustos.distribuicaoCustos.map((item) => (
            <div key={item.tipo}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 capitalize">{item.tipo}</span>
                <span className="text-sm font-semibold text-gray-900">
                  R$ {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${item.percentual}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{item.percentual.toFixed(1)}% do total</p>
            </div>
          ))}
        </div>
      </div>

      {/* Custo por Peso Comercial */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-scales-line text-orange-600"></i>
          Estimativa por Peso Comercial
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analiseCustos.custoPorPesoComercial).map(([peso, custo]) => (
            <div key={peso} className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-2">{peso}</p>
              <p className="text-xl font-bold text-gray-900">
                R$ {custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Resumo Executivo */}
      <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-file-list-line text-orange-600"></i>
          Resumo Executivo
        </h3>
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            <strong>Investimento Total:</strong> R$ {analiseCustos.custoTotalRecompensas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} 
            para {campanha.participantes.length} participantes
          </p>
          <p>
            <strong>Custo Médio por Participante:</strong> R$ {analiseCustos.custoMedioPorParticipante.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
          <p>
            <strong>Complexidade da Campanha:</strong> {analiseComplexidade.nivelComplexidade} 
            ({analiseComplexidade.totalMetas} metas, {analiseComplexidade.totalDistintivos} distintivos, {analiseComplexidade.totalMissoes} missões)
          </p>
          <p>
            <strong>Período:</strong> {new Date(campanha.dataInicio).toLocaleDateString('pt-BR')} até {new Date(campanha.dataFim).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>
    </div>
  );
};
